
import React, { useState, useEffect } from "react";
import { useAuth } from "../AuthContext";
import AddProduct from "./AddProduct";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminOrders from "./AdminOrders";
import UserManager from "./UserManager";
import AdminSlider from "./AdminSlider"; // Import Slider component

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("addproduct"); // Set default tab to "addproduct"
  const [loading, setLoading] = useState(false); // Set initial loading to false
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!auth || !auth.isAdmin) {
      navigate('/login');
      toast.error('You need admin privileges to access this page');
    }
  }, [auth, navigate]);

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate("/login"); // Redirect to login page
  };

  if (!auth || !auth.isAdmin) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "#f5f7fa"
      }}>
        <div style={{
          textAlign: "center",
          padding: "40px",
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
        }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to view this page</p>
          <button 
            onClick={() => navigate("/login")}
            style={{
              padding: "10px 20px",
              background: "#2874f0",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginTop: "20px"
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      {/* Sidebar */}
      <div style={{
        width: "280px",
        background: "linear-gradient(180deg, #2874f0 0%, #1a5dc8 100%)",
        color: "white",
        padding: "20px 0",
        boxShadow: "2px 0 10px rgba(0,0,0,0.1)",
        position: "fixed",
        height: "100vh",
        zIndex: 100
      }}>
        <div style={{
          padding: "0 20px 20px",
          borderBottom: "1px solid rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          gap: "10px"
        }}>
          <div style={{
            width: "40px",
            height: "40px",
            background: "rgba(255,255,255,0.2)",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px"
          }}>
            🛒
          </div>
          <h2 style={{
            margin: 0,
            fontSize: "20px",
            fontWeight: 600,
            background: "linear-gradient(to right, #fff 0%, #d1e0ff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}>
            Admin Panel
          </h2>
        </div>
        <nav>
          <ul style={{ listStyle: "none", padding: 0, marginTop: "20px" }}>
            {[
              { id: "addproduct", icon: "🛍️", label: "Add Product" },
              { id: "users", icon: "👥", label: "Users" },
              { id: "orders", icon: "📦", label: "Orders" },
              { id: "Slider", icon: "📊", label: "Slider" }
            ].map((item) => (
              <li
                key={item.id}
                style={{
                  padding: "14px 20px",
                  cursor: "pointer",
                  background: activeTab === item.id ? "rgba(255,255,255,0.25)" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden",
                  ":hover": {
                    background: "rgba(255,255,255,0.15)"
                  }
                }}
                onClick={() => setActiveTab(item.id)}
              >
                <span style={{ fontSize: "18px" }}>{item.icon}</span>
                <span style={{ fontWeight: activeTab === item.id ? 600 : 400 }}>
                  {item.label}
                </span>
                {activeTab === item.id && (
                  <span style={{
                    marginLeft: "auto",
                    fontSize: "12px",
                    background: "rgba(255,255,255,0.3)",
                    padding: "2px 8px",
                    borderRadius: "10px"
                  }}>
                    Active
                  </span>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        padding: "30px",
        marginLeft: "280px",
        overflowY: "auto"
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "30px",
          paddingBottom: "20px",
          borderBottom: "1px solid rgba(0,0,0,0.1)"
        }}>
          <h1 style={{
            margin: 0,
            fontSize: "28px",
            fontWeight: 700,
            color: "#1a237e"
          }}>
            {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          </h1>
          <button
            onClick={handleLogout}
            style={{
              background: "linear-gradient(135deg, #ff5252, #ff1744)",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.3s ease",
              ":hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 15px rgba(255, 23, 68, 0.4)"
              }
            }}
          >
            Logout
          </button>
        </div>

        {/* Content Area */}
        <div style={{
          background: "white",
          borderRadius: "12px",
          padding: "20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
        }}>
          {activeTab === "addproduct" && <AddProduct />}
          {activeTab === "users" && <UserManager />}
          {activeTab === "orders" && <AdminOrders />}
          {activeTab === "Slider" && <AdminSlider />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;